import {controller, target, targets, attr} from '@github/catalyst'
import {reportError} from '@github-ui/failbot'
import {toggleToast} from '@github-ui/toggle-toast'
import {fetchSafeDocumentFragment} from '@github-ui/fetch-utils'
import type {ToggleSwitchElement} from '../../../../../components/primer/experimental/toggle-switch-element'

export interface IFeedFilterValue {
  Announcements: boolean
  Releases: boolean
  Sponsors: boolean
  Stars: boolean
  Repositories: boolean
  Follows: boolean
  Recommendations: boolean
}

@controller
export class FeedFilterElement extends HTMLElement {
  @attr value: string
  @attr org: string
  @target csrfToken: HTMLInputElement
  @target applyButton: HTMLButtonElement
  @targets inputs: HTMLInputElement[]

  filterCache: Map<string, string> = new Map()
  initialFilterValue: IFeedFilterValue

  public get filterValues(): IFeedFilterValue {
    return JSON.parse(this.value)
  }

  connectedCallback() {
    this.initialFilterValue = this.filterValues
  }

  handleToggle(e: Event) {
    if (!(e.target instanceof HTMLInputElement)) {
      e.preventDefault()
    }

    const item = e.currentTarget as HTMLElement
    // capturing input elements for label and toggle-switch
    const input = item.querySelector('input') || (item as HTMLInputElement)
    const filter = this.filterValues
    const filterName = item.getAttribute('data-name') as keyof IFeedFilterValue

    if (filter[filterName] === undefined) return
    filter[filterName] = !filter[filterName]

    input.checked = filter[filterName]
    input.toggleAttribute('checked', filter[filterName])

    this.setFilterValue(filter)
    this.updateApplyButtonHydroAttributes(filter)
  }

  async handleApply() {
    let CONDUIT_FILTER_FEED_URL = '/conduit/filter'
    const activeTopic = this.getActiveTopic()

    if (this.isTopicFeed(activeTopic)) {
      CONDUIT_FILTER_FEED_URL = `${CONDUIT_FILTER_FEED_URL}?is_topic=true&topic=${activeTopic}`
    } else if (this.org) {
      CONDUIT_FILTER_FEED_URL = `${CONDUIT_FILTER_FEED_URL}?org=${this.org}`
    }

    try {
      const response = await fetch(CONDUIT_FILTER_FEED_URL, {
        method: 'PUT',
        body: JSON.stringify(this.filterValues),
        credentials: 'same-origin',
        headers: {
          'Scoped-CSRF-Token': this.csrfToken.value,
          'X-Requested-With': 'XMLHttpRequest',
        },
      })

      const html = await response.text()

      this.dispatchEvent(
        new CustomEvent('feed-filter:apply', {
          bubbles: true,
          detail: {
            updatedFeedContent: html,
          },
        }),
      )
    } catch (err: unknown) {
      reportError(err)
      toggleToast(document.querySelector('.js-feed-filter-error-toast')!.innerHTML)
      this.resetFilterValue()
    }
  }

  public setFilterValue(value: IFeedFilterValue) {
    this.value = JSON.stringify(value)
  }

  private resetFilterValue() {
    for (const input of this.inputs) {
      const groupName = (input.name || input.getAttribute('data-name')) as keyof IFeedFilterValue

      const value = this.initialFilterValue[groupName]
      input.toggleAttribute('checked', value)
    }

    this.setFilterValue(this.initialFilterValue)
  }

  private reloadFilterValues(cachedContent: string) {
    this.value = cachedContent
    const filter = this.filterValues
    for (const input of this.inputs) {
      const name = input.getAttribute('name') as keyof IFeedFilterValue
      switch (input.tagName) {
        case 'INPUT':
          input.checked = filter[name]
          break
        default:
          throw new Error(`Unexpected tag name: ${input.tagName}`)
      }
    }

    this.setFilterValue(filter)
  }

  private updateApplyButtonHydroAttributes(filter: IFeedFilterValue) {
    const attrs = this.applyButton.getAttribute('data-hydro-click') as string
    const attrsObj = JSON.parse(attrs)
    attrsObj.payload.metadata.filter_groups = Object.entries(filter)
      .filter((kv: [string, unknown]) => kv[1])
      .map((kv: [string, unknown]) => kv[0])
    this.applyButton.setAttribute('data-hydro-click', JSON.stringify(attrsObj))
  }

  private getUrl() {
    return new URL(window.location.href, window.location.origin)
  }

  private getUrlParams() {
    const url = this.getUrl()
    const params = new URLSearchParams(url.search)
    return params
  }

  private isTopicFeed(topic: string) {
    return topic !== 'for-you' && !this.org
  }

  async setFeedFilterSettings(previousTopic: string | null = null) {
    let CONDUIT_FILTER_FEED_URL = '/conduit/filter'

    if (previousTopic) {
      this.cacheFilter(previousTopic)
    }

    const activeTopic = this.getActiveTopic()
    const cachedContent = this.filterCache.get(this.getFilterCacheKey(activeTopic))

    if (cachedContent) {
      this.reloadFilterValues(cachedContent)
    } else {
      if (this.isTopicFeed(activeTopic)) {
        CONDUIT_FILTER_FEED_URL = `${CONDUIT_FILTER_FEED_URL}?is_topic=true&topic=${activeTopic}`
      } else if (this.org) {
        CONDUIT_FILTER_FEED_URL = `${CONDUIT_FILTER_FEED_URL}?org=${this.org}`
      }

      const fragment = await fetchSafeDocumentFragment(document, CONDUIT_FILTER_FEED_URL)
      this.innerHTML = fragment.querySelector(this.tagName)!.innerHTML
      this.value = fragment.querySelector(this.tagName)!.getAttribute('data-value')!
    }
  }

  private getActiveTopic() {
    if (this.org) {
      return this.org
    }

    const topic = this.getUrlParams().get('topic')
    return topic || 'for-you'
  }

  cacheFilter(topic: string) {
    this.filterCache.set(this.getFilterCacheKey(topic), this.value)
  }

  private getFilterCacheKey(topic: string) {
    switch (topic) {
      case 'for-you':
        return 'for-you'
      case 'topic':
        return 'topic'
      default:
        return topic
    }
  }
}
